import { ValidationError } from '@domain/errors';
import type {
  ArticleCategoryDraft,
  ArticleCategoryRef,
  City,
  Country,
  CountryDraft,
  Formula,
  FormulaDraft,
  MobileConfig,
  NotificationLogEntry,
  NotificationTemplate,
  NotificationTemplates,
  ReminderSettings,
} from '@domain/entities';
import type {
  ArticleCategoryRepository,
  CityRepository,
  CountryRepository,
  FormulaRepository,
  MobileConfigRepository,
  NotificationRepository,
} from '@domain/repositories';
import type { NotificationType } from '@domain/value-objects';
import type { AccessPolicy } from '../../ports';
import type { UseCase } from '../use-case';

/* ------------------------------ Formules ------------------------------ */

export class ListFormulas implements UseCase<void, Formula[]> {
  constructor(private readonly repo: FormulaRepository) {}
  execute(): Promise<Formula[]> {
    return this.repo.list();
  }
}

export interface SaveFormulaInput {
  id: string | null;
  draft: FormulaDraft;
}

export class SaveFormula implements UseCase<SaveFormulaInput, Formula> {
  constructor(
    private readonly repo: FormulaRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute({ id, draft }: SaveFormulaInput): Promise<Formula> {
    this.policy.assertSuperAdmin('gérer les formules');
    const fields: Record<string, string> = {};
    if (!draft.label.trim()) fields.label = 'Le libellé est requis';
    if (draft.amountXof < 0) fields.amountXof = 'Le prix doit être positif';
    if (draft.durationDays < 1) fields.durationDays = 'Durée minimale : 1 jour';
    if (Object.keys(fields).length > 0) {
      throw new ValidationError('Formulaire incomplet', fields);
    }
    const normalized: FormulaDraft = { ...draft, label: draft.label.trim() };
    return id === null ? this.repo.create(normalized) : this.repo.update(id, normalized);
  }
}

export class DeleteFormula implements UseCase<string, void> {
  constructor(
    private readonly repo: FormulaRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute(id: string): Promise<void> {
    this.policy.assertSuperAdmin('supprimer une formule');
    await this.repo.remove(id);
  }
}

export class GetReminderSettings implements UseCase<void, ReminderSettings> {
  constructor(private readonly repo: FormulaRepository) {}
  execute(): Promise<ReminderSettings> {
    return this.repo.getReminder();
  }
}

export class SaveReminderSettings implements UseCase<ReminderSettings, ReminderSettings> {
  constructor(
    private readonly repo: FormulaRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute(settings: ReminderSettings): Promise<ReminderSettings> {
    this.policy.assertSuperAdmin('modifier la relance automatique');
    if (settings.daysBefore < 1 || settings.daysBefore > 90) {
      throw new ValidationError('Délai invalide', { daysBefore: 'Entre 1 et 90 jours' });
    }
    return this.repo.saveReminder(settings);
  }
}

/* -------------------------------- Villes -------------------------------- */

export class ListCities implements UseCase<void, City[]> {
  constructor(private readonly repo: CityRepository) {}
  execute(): Promise<City[]> {
    return this.repo.list();
  }
}

/* -------------------------------- Pays -------------------------------- */

export class ListCountries implements UseCase<void, Country[]> {
  constructor(private readonly repo: CountryRepository) {}
  execute(): Promise<Country[]> {
    return this.repo.list();
  }
}

export interface SaveCountryInput {
  id: string | null;
  draft: CountryDraft;
}

export class SaveCountry implements UseCase<SaveCountryInput, Country> {
  constructor(
    private readonly repo: CountryRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute({ id, draft }: SaveCountryInput): Promise<Country> {
    this.policy.assertSuperAdmin('gérer les pays');
    const fields: Record<string, string> = {};
    if (!draft.name.trim()) fields.name = 'Le nom est requis';
    if (!/^\+\d{1,4}$/.test(draft.code)) fields.code = 'Indicatif invalide (ex: +225)';
    if (Object.keys(fields).length > 0) {
      throw new ValidationError('Formulaire incomplet', fields);
    }
    const normalized: CountryDraft = { ...draft, name: draft.name.trim() };
    return id === null ? this.repo.create(normalized) : this.repo.update(id, normalized);
  }
}

export class ToggleCountry implements UseCase<string, Country> {
  constructor(
    private readonly repo: CountryRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute(id: string): Promise<Country> {
    this.policy.assertSuperAdmin('activer ou désactiver un pays');
    return this.repo.toggleActive(id);
  }
}

export class DeleteCountry implements UseCase<string, void> {
  constructor(
    private readonly repo: CountryRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute(id: string): Promise<void> {
    this.policy.assertSuperAdmin('supprimer un pays');
    await this.repo.remove(id);
  }
}

/* ---------------------------- Notifications --------------------------- */

export class GetNotificationTemplates implements UseCase<void, NotificationTemplates> {
  constructor(private readonly repo: NotificationRepository) {}
  execute(): Promise<NotificationTemplates> {
    return this.repo.getTemplates();
  }
}

export interface SaveNotificationTemplateInput {
  type: NotificationType;
  patch: Partial<NotificationTemplate>;
}

export class SaveNotificationTemplate
  implements UseCase<SaveNotificationTemplateInput, NotificationTemplates>
{
  constructor(private readonly repo: NotificationRepository) {}
  execute({ type, patch }: SaveNotificationTemplateInput): Promise<NotificationTemplates> {
    return this.repo.saveTemplate(type, patch);
  }
}

export class ListNotificationLog implements UseCase<number, NotificationLogEntry[]> {
  constructor(private readonly repo: NotificationRepository) {}
  execute(limit: number): Promise<NotificationLogEntry[]> {
    return this.repo.listLog(limit);
  }
}

/* -------------------- Catégories d'articles -------------------- */

export class ListArticleCategories implements UseCase<void, ArticleCategoryRef[]> {
  constructor(private readonly repo: ArticleCategoryRepository) {}
  execute(): Promise<ArticleCategoryRef[]> { return this.repo.list(); }
}

export interface SaveArticleCategoryInput {
  id: string | null;
  draft: ArticleCategoryDraft;
}

export class SaveArticleCategory implements UseCase<SaveArticleCategoryInput, ArticleCategoryRef> {
  constructor(
    private readonly repo: ArticleCategoryRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute({ id, draft }: SaveArticleCategoryInput): Promise<ArticleCategoryRef> {
    this.policy.assertSuperAdmin('gérer les catégories d\'articles');
    const fields: Record<string, string> = {};
    if (!draft.label.trim()) fields.label = 'Le libellé est requis';
    if (!draft.slug.trim()) fields.slug = 'Le slug est requis';
    if (Object.keys(fields).length > 0) throw new ValidationError('Formulaire incomplet', fields);
    return id === null ? this.repo.create(draft) : this.repo.update(id, draft);
  }
}

export class ToggleArticleCategory implements UseCase<{ id: string; currentIsActive: boolean }, ArticleCategoryRef> {
  constructor(
    private readonly repo: ArticleCategoryRepository,
    private readonly policy: AccessPolicy,
  ) {}
  execute({ id, currentIsActive }: { id: string; currentIsActive: boolean }): Promise<ArticleCategoryRef> {
    this.policy.assertSuperAdmin('activer ou désactiver une catégorie');
    return this.repo.toggleActive(id, currentIsActive);
  }
}

export class DeleteArticleCategory implements UseCase<string, void> {
  constructor(
    private readonly repo: ArticleCategoryRepository,
    private readonly policy: AccessPolicy,
  ) {}
  async execute(id: string): Promise<void> {
    this.policy.assertSuperAdmin('supprimer une catégorie d\'articles');
    await this.repo.remove(id);
  }
}

/* -------------------- Configuration mobile -------------------- */

export class GetMobileConfig implements UseCase<void, MobileConfig> {
  constructor(private readonly repo: MobileConfigRepository) {}
  execute(): Promise<MobileConfig> {
    return this.repo.get();
  }
}

export interface SaveMobileConfigInput {
  config: MobileConfig;
}

export class SaveMobileConfig implements UseCase<SaveMobileConfigInput, MobileConfig> {
  constructor(
    private readonly repo: MobileConfigRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute({ config }: SaveMobileConfigInput): Promise<MobileConfig> {
    this.policy.assertSuperAdmin('modifier la configuration mobile');
    const VERSION_REGEX = /^\d+\.\d+\.\d+$/;
    const fields: Record<string, string> = {};
    if (!VERSION_REGEX.test(config.iosMinVersion)) fields.iosMinVersion = 'Format attendu : X.Y.Z';
    if (!VERSION_REGEX.test(config.androidMinVersion)) fields.androidMinVersion = 'Format attendu : X.Y.Z';
    if (Object.keys(fields).length > 0) throw new ValidationError('Version invalide', fields);
    return this.repo.save(config);
  }
}
